import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Shield, RotateCcw, Save } from 'lucide-react';
import toast from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PermissionsDialog({ user, open, onClose, onSave }) {
  const [permissions, setPermissions] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [permissionLabels, setPermissionLabels] = useState({});
  const [defaultPermissions, setDefaultPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [hasCustom, setHasCustom] = useState(false);

  useEffect(() => {
    if (open && user) {
      fetchPermissionsData();
    }
  }, [open, user]);

  const fetchPermissionsData = async () => {
    try {
      // Fetch available permissions
      const availableRes = await axios.get(`${API}/admin/permissions`);
      setAvailablePermissions(availableRes.data.available_permissions);
      setPermissionLabels(availableRes.data.permission_labels);
      setDefaultPermissions(availableRes.data.default_permissions);

      // Fetch user's current permissions
      const userPermsRes = await axios.get(`${API}/admin/users/${user.id}/permissions`);
      setPermissions(userPermsRes.data.effective_permissions);
      setHasCustom(userPermsRes.data.has_custom_permissions);
    } catch (error) {
      toast.error('Error al cargar permisos');
    }
  };

  const handleTogglePermission = (permission) => {
    setPermissions(prev => {
      if (prev.includes(permission)) {
        return prev.filter(p => p !== permission);
      } else {
        return [...prev, permission];
      }
    });
    setHasCustom(true);
  };

  const handleResetToDefault = () => {
    const roleDefaults = defaultPermissions[user.role] || [];
    setPermissions(roleDefaults);
    setHasCustom(false);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/users/${user.id}`, {
        email: user.email,
        name: user.name,
        role: user.role,
        supervisor_id: user.supervisor_id || null,
        permissions: hasCustom ? permissions : null
      });
      toast.success('Permisos actualizados exitosamente');
      onSave && onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar permisos');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  // Group permissions by category
  const permissionGroups = {
    'Pagos': ['ver_pagos', 'crear_pagos', 'aprobar_pagos', 'rechazar_pagos', 'eliminar_pagos'],
    'Reportes': ['ver_reportes', 'exportar_reportes', 'ver_reporte_detallado', 'ver_reporte_macrobase', 'ver_reporte_contado_vencido', 'ver_reporte_compras_proveedor', 'descargar_reporte_compras_proveedor'],
    'Adelantos (Bolsón)': ['ver_adelantos', 'crear_adelanto', 'aplicar_adelanto', 'cerrar_adelanto', 'elegir_beneficiario_adelanto', 'revertir_adelanto', 'gestionar_sync_flujo'],
    'ERP': ['marcar_macrobase', 'ver_filtro_macrobase'],
    'Gestión': ['gestionar_usuarios', 'gestionar_proveedores', 'gestionar_tiendas', 'gestionar_bancos'],
    'Otros': ['ver_boletas', 'ver_configuracion', 'enviar_whatsapp']
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-indigo-600" />
            Permisos de {user.name}
          </DialogTitle>
          <DialogDescription>
            Rol: <Badge variant="outline">{user.role}</Badge>
            {hasCustom && (
              <Badge className="ml-2 bg-orange-100 text-orange-700">Permisos personalizados</Badge>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {Object.entries(permissionGroups).map(([groupName, groupPerms]) => (
            <div key={groupName} className="space-y-3">
              <h3 className="font-semibold text-slate-700 border-b pb-1">{groupName}</h3>
              <div className="grid grid-cols-2 gap-3">
                {groupPerms.map(perm => (
                  <div key={perm} className="flex items-center space-x-2">
                    <Checkbox
                      id={perm}
                      checked={permissions.includes(perm)}
                      onCheckedChange={() => handleTogglePermission(perm)}
                      disabled={user.role === 'admin'}
                    />
                    <Label 
                      htmlFor={perm} 
                      className={`text-sm cursor-pointer ${permissions.includes(perm) ? 'text-slate-900' : 'text-slate-500'}`}
                    >
                      {permissionLabels[perm] || perm}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {user.role === 'admin' && (
            <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
              ⚠️ El administrador tiene todos los permisos por defecto y no se pueden modificar.
            </p>
          )}
        </div>

        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleResetToDefault}
            disabled={user.role === 'admin'}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Restaurar por defecto
          </Button>
          <div className="space-x-2">
            <Button variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={loading || user.role === 'admin'}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Save className="h-4 w-4 mr-2" />
              {loading ? 'Guardando...' : 'Guardar Permisos'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
